import { useCallback, useEffect, useState } from 'react';
import { Flex } from 'antd';

import {
  useAppSelector,
  useRequestTypeFinderById,
  useStatusFinderByNum,
  useUserPermissions,
} from '@Hooks/index';
import {
  useDownloadDocumentQuery,
  useFormListMutation,
} from '@Store/api/dmsApiSlice';
import { useGetHrmRequestFormQuery } from '@Store/api/basicHrmApiSlice';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { getDocument } from '@Features/utils/document';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { useGetEmpPhotoQuery } from '@Store/api/dictionaryApiSlice';

import fieldSource from '../FieldSource';
import { getDocumentFolder } from '../../utils/document';
import { EditAction, FooterActions } from './components';
import styles from './view-mobile-form.module.css';

interface RequestViewMobileFormProps {
  onEdit: (recordKey: string) => void;
  onDeleteRequest: (requestNo: string) => void;
  onSubmitRequest: (requestNo: string) => void;
  onCancelRequest: (requestNo: string) => void;
}

const RequestViewMobileForm: React.FC<RequestViewMobileFormProps> = ({
  onEdit,
  onDeleteRequest,
  onSubmitRequest,
  onCancelRequest,
}) => {
  const { findStatus } = useStatusFinderByNum('Leave');
  const { findRequestType } = useRequestTypeFinderById('Leave');
  const permissions = useUserPermissions();
  const requestId = useAppSelector(getModalRecordKey);
  const { data } = useGetHrmRequestFormQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });
  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const [getFormList] = useFormListMutation();

  const [documentId, setDocumentId] = useState('');

  const { data: imageURL } = useDownloadDocumentQuery(documentId || '', {
    skip: !documentId,
  });

  const replacementUser = users.find(
    (item) => item.empNumber === data?.replacementEmpNo
  );

  const { data: userPhoto } = useGetEmpPhotoQuery(
    replacementUser?.empImageName || ''
  );

  useEffect(() => {
    if (!data?.requestId || !data?.filepath) {
      return;
    }

    const documentFolderPath = getDocumentFolder(data.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, data.filepath);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [data]);

  const handleDownloadClick = useCallback((): void => {
    if (imageURL && data?.filepath) {
      const a = document.createElement('a');
      a.href = imageURL;
      a.download = data.filepath;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageURL, data?.filepath]);

  if (!data) return null;

  const statusRequest = findStatus(data?.reqStatusId)?.codeDesc || '';

  return (
    <Flex vertical>
      <div className={styles.viewMobileForm}>
        {fieldSource(
          data,
          findRequestType,
          findStatus,
          documentId,
          users,
          userPhoto,
          replacementUser,
          handleDownloadClick
        ).map((item) => (
          <div className={styles.viewMobileItem} key={item.label}>
            <span>{item.label}</span>
            <span className={styles.viewMobileItemValue}>{item.value}</span>
          </div>
        ))}
      </div>
      <EditAction handleEdit={() => onEdit(requestId)} status={statusRequest} />
      <FooterActions
        handleDeleteRequest={() => onDeleteRequest(requestId)}
        handleSubmitRequest={() => onSubmitRequest(requestId)}
        handleCancelRequest={() => onCancelRequest(requestId)}
        disabledCancelForm={!permissions.can('Cancel', 'Leave')}
        disabledDeleteForm={!permissions.can('Delete', 'Leave')}
        disabledSubmitForm={!permissions.can('Submit', 'Leave')}
        status={statusRequest}
      />
    </Flex>
  );
};

export default RequestViewMobileForm;
